import React, { useEffect, useState } from "react";
import { Typography, CircularProgress } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Pagination from "components/Pagination/Pagination.js";
import ProfileCard from "./ProfileCard";

import { SearchProvider, useSearch } from "context/SearchContext";
import { useAuth } from "context/AuthContext";
import AdmirersApi from "api_calls/AdmirersApi";

import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";

const useStyles = makeStyles(styles);

export default function Admirers() {
  const classes = useStyles();
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [likedIds, setLikedIds] = useState([]);
  const [blockedIds, setBlockedIds] = useState([]);

  const { searchQuery } = useSearch();
  const { user } = useAuth();

  useEffect(() => {
    const fetchProfiles = async () => {
      setLoading(true);

      const gender = user?.gender || "MALE";
      const userId = user?.user_id || "";

      try {
        const response = await AdmirersApi.fetchProfiles(
          gender,
          userId,
          currentPage,
          searchQuery
        );

        if (response) {
          setProfiles(response.circles || []);
          setTotalPages(response.total_pages || 1);
          setLikedIds(response.liked_ids || []);
          setBlockedIds(response.blocked_ids || []);
        } else {
          setProfiles([]);
          setLikedIds([]);
          setBlockedIds([]);
        }
      } catch (error) {
        console.error("Error fetching profiles:", error);
        setProfiles([]);
        setLikedIds([]);
        setBlockedIds([]);
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchProfiles();
    }
  }, [currentPage, searchQuery, user]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleBlockSuccess = (blockedUserId) => {
    setProfiles((prev) => prev.filter((p) => p.user_id !== blockedUserId));
    setBlockedIds((prev) => prev.filter((id) => id !== blockedUserId));
  };

  return (
    <SearchProvider>
      <>
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "20px",
            fontFamily: "Calibri, sans-serif",
          }}
        >
          My Admirers 🔥
        </Typography>
        <br />

        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "20px",
            }}
          >
            <CircularProgress color="primary" />
          </div>
        ) : (
          <GridContainer>
            {profiles.length > 0 ? (
              profiles.map((profile) => {
                const isLiked = likedIds.includes(profile.user_id);
                const isBlocked = blockedIds.includes(profile.user_id);

                return (
                  <GridItem key={profile.user_id} xs={12} sm={12} md={3}>
                    <ProfileCard
                      profile={profile}
                      context="admirers"
                      onBlockSuccess={handleBlockSuccess}
                      initialLiked={isLiked}
                      initialBlocked={isBlocked}
                    />
                  </GridItem>
                );
              })
            ) : (
              <GridItem xs={12}>
                <h4>No profiles found</h4>
              </GridItem>
            )}
          </GridContainer>
        )}

        <GridContainer justify="center">
          <Pagination
            pages={[
              {
                text: "PREV",
                onClick: () => handlePageChange(currentPage - 1),
                disabled: currentPage === 1,
              },
              ...Array.from({ length: totalPages }, (_, i) => ({
                text: (i + 1).toString(),
                active: i + 1 === currentPage,
                onClick: () => handlePageChange(i + 1),
              })),
              {
                text: "NEXT",
                onClick: () => handlePageChange(currentPage + 1),
                disabled: currentPage === totalPages,
              },
            ]}
          />
        </GridContainer>
      </>
    </SearchProvider>
  );
}
